import { z } from 'zod'
import {
  AnalyticsEventIdSchema,
  CompanyIdSchema,
  ContactIdSchema,
  WorkspaceIdSchema,
} from '../ids/index'
import { FilterExprSchema } from './grid-filter'

export const AnalyticsEventSourceSchema = z.string().min(1)
export type AnalyticsEventSource = z.infer<typeof AnalyticsEventSourceSchema>

export const EventPropertyOperatorSchema = z.enum([
  'eq',
  'neq',
  'contains',
  'not_contains',
  'gt',
  'gte',
  'lt',
  'lte',
  'exists',
  'not_exists',
])
export type EventPropertyOperator = z.infer<typeof EventPropertyOperatorSchema>

export const EventPropertyFilterSchema = z.object({
  property: z.string().min(1).optional(),
  operator: EventPropertyOperatorSchema.default('eq'),
  value: z.union([z.string(), z.number(), z.boolean()]).optional(),
})
export type EventPropertyFilter = z.infer<typeof EventPropertyFilterSchema>

export const EventRecordFilterSchema = z.object({
  eventName: z.string().min(1).optional(),
  withinDays: z.number().int().min(0).max(3650).optional(),
  since: z.string().datetime().optional(),
  until: z.string().datetime().optional(),
  property: EventPropertyFilterSchema.optional(),
})
export type EventRecordFilter = z.infer<typeof EventRecordFilterSchema>

export const AnalyticsEventSchema = z.object({
  id: AnalyticsEventIdSchema,
  workspaceId: WorkspaceIdSchema,
  source: AnalyticsEventSourceSchema.default('event_store'),
  sourceEventId: z.string().optional(),
  eventName: z.string().min(1),
  occurredAt: z.string().datetime(),
  distinctId: z.string().optional(),
  email: z.string().email().optional(),
  contactId: ContactIdSchema.optional(),
  companyId: CompanyIdSchema.optional(),
  url: z.string().optional(),
  pageTitle: z.string().optional(),
  properties: z.record(z.unknown()).default({}),
  rawPayload: z.record(z.unknown()).default({}),
  syncedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),

  // Read-only display fields resolved by live list queries.
  contactName: z.string().optional(),
  companyName: z.string().optional(),
})
export type AnalyticsEvent = z.infer<typeof AnalyticsEventSchema>

export const AnalyticsEventListQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
  source: AnalyticsEventSourceSchema.optional(),
  eventName: z.string().optional(),
  contactId: ContactIdSchema.optional(),
  companyId: CompanyIdSchema.optional(),
  email: z.string().email().optional(),
  withinDays: z.number().int().min(0).max(3650).optional(),
  since: z.string().datetime().optional(),
  until: z.string().datetime().optional(),
  search: z.string().optional(),
  filter: FilterExprSchema.optional(),
})
export type AnalyticsEventListQuery = z.infer<typeof AnalyticsEventListQuerySchema>

export const AnalyticsEventPropertyTypeSchema = z.enum([
  'string',
  'number',
  'boolean',
  'date',
  'json',
  'mixed',
])
export type AnalyticsEventPropertyType = z.infer<typeof AnalyticsEventPropertyTypeSchema>

export const AnalyticsEventFilterMetadataInputSchema = z.object({
  /** Search event names without forcing users to type an exact event from scratch. */
  search: z.string().optional(),
  /** When present, return event-property keys scoped to this event only. */
  eventName: z.string().min(1).optional(),
  propertySearch: z.string().optional(),
  propertyName: z.string().min(1).optional(),
  valueSearch: z.string().optional(),
  limit: z.number().int().min(1).max(1000).default(250),
  propertyLimit: z.number().int().min(1).max(1000).default(250),
  valueLimit: z.number().int().min(1).max(100).default(25),
})
export type AnalyticsEventFilterMetadataInput = z.infer<
  typeof AnalyticsEventFilterMetadataInputSchema
>

export const AnalyticsEventNameMetaSchema = z.object({
  eventName: z.string().min(1),
  count: z.number().int().nonnegative(),
  lastSeenAt: z.string().datetime().optional(),
})
export type AnalyticsEventNameMeta = z.infer<typeof AnalyticsEventNameMetaSchema>

export const AnalyticsEventPropertyValueMetaSchema = z.object({
  value: z.string(),
  count: z.number().int().nonnegative(),
})
export type AnalyticsEventPropertyValueMeta = z.infer<typeof AnalyticsEventPropertyValueMetaSchema>

export const AnalyticsEventPropertyMetaSchema = z.object({
  property: z.string().min(1),
  type: AnalyticsEventPropertyTypeSchema,
  count: z.number().int().nonnegative(),
  lastSeenAt: z.string().datetime().optional(),
  sampleValues: z.array(AnalyticsEventPropertyValueMetaSchema).default([]),
})
export type AnalyticsEventPropertyMeta = z.infer<typeof AnalyticsEventPropertyMetaSchema>

export const AnalyticsEventFilterMetadataOutputSchema = z.object({
  eventNames: z.array(AnalyticsEventNameMetaSchema),
  properties: z.array(AnalyticsEventPropertyMetaSchema).default([]),
})
export type AnalyticsEventFilterMetadataOutput = z.infer<
  typeof AnalyticsEventFilterMetadataOutputSchema
>
